import { useMutation } from '@tanstack/react-query'
import { api, ApiError } from '@/internationalization/api-client'

export interface SupportRequestInput {
  subject: string
  description: string
  attachments: File[]
}

export interface SupportRequestAccepted {
  requestId: string
  attachmentCount: number
  /** False where the server only rendered the message (a local `blob` transport). */
  delivered: boolean
}

/**
 * The body carries a subject, a description and files -- and nothing that says
 * who is asking or who should receive it. Identity comes from the session and
 * the recipients from the server's configuration, so there is no field here a
 * caller could use to change either.
 */
export function buildSupportForm({ subject, description, attachments }: SupportRequestInput): FormData {
  const form = new FormData()
  form.append('subject', subject.trim())
  form.append('description', description.trim())
  for (const file of attachments) form.append('attachments', file, file.name)
  return form
}

/**
 * A 202 is "accepted", not "delivered": the server also answers 202 when it only
 * rendered the message (a dry-run transport). Anything but an exact `true` --
 * false, a missing field, a string -- is a request that reached nobody, so it is
 * raised as a failure and goes through the modal's error path.
 */
export function requireDelivered(response: SupportRequestAccepted): SupportRequestAccepted {
  if (response?.delivered === true) return response
  throw new ApiError(
    202,
    'support_not_delivered',
    'The server accepted the request but did not deliver it.',
  )
}

export const supportApi = {
  send: async (input: SupportRequestInput) =>
    requireDelivered(
      await api.post<SupportRequestAccepted>('/support/requests', buildSupportForm(input)),
    ),
}

/**
 * No retry, on purpose. A send whose outcome is ambiguous (a timeout after the
 * request left the server) may already have been delivered, so the person is
 * shown the failure and decides -- an automatic second attempt could mail three
 * people twice.
 */
export function useSendSupportRequest() {
  return useMutation({ mutationFn: supportApi.send, retry: false })
}
